import React, { useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { deprecate } from 'react-is-deprecated';
import { useDevice } from '@atc/react-device';
import BottomSheet from '../BottomSheet/index.js';
import DesktopPopover from './DesktopPopover.js';

/**
 * A responsive container component that displays content with a primary CTA button at the bottom of the sheet. On
 * desktop, it displays content in an elevated popover anchored relative to a trigger element (such as `SelectButton`).
 * On mobile devices, it renders a sliding bottom sheet with a heading and close button that animates up from the bottom
 * of the viewport. A callback is provided for the CTA button, close button, and clicks outside the sheet.
 */
import { jsx as _jsx } from "react/jsx-runtime";
function PopoverSheet(_ref) {
  let {
    borderColor = 'transparent',
    buttonLabel = 'Done',
    children,
    className,
    heading,
    onClick,
    // deprecated
    onHide = () => {},
    containerElement,
    show,
    position,
    hideCTA = true,
    fullWidth = true,
    renderServerSide = false,
    popoverTriggerId = '',
    useBottomSheet = true,
    ...rest
  } = _ref;
  const popoverRef = useRef(null);
  const handleHideClick = useCallback(() => {
    // handle deprecated onClick callback for onHide functionaliuty
    if (typeof onClick === 'function') {
      onClick();
    }
    onHide();
  }, [onClick, onHide]);
  const handleDocumentClick = useCallback(event => {
    // Only trigger close if the sheet has been attached to the DOM (has ref)
    // and the click happened outside of the sheet and trigger
    if (popoverRef.current) {
      const path = event.composedPath ? event.composedPath() : [event.target];
      const showPopoverCTA = document.getElementById(popoverTriggerId);
      const clickedInsidePopover = path.some(el => el && el.nodeType === 1 && popoverRef.current.contains(el));
      const clickedInsideTrigger = showPopoverCTA ? path.some(el => el && el.nodeType === 1 && showPopoverCTA.contains(el)) : false;
      if (!clickedInsidePopover && !clickedInsideTrigger) {
        handleHideClick();
      }
    }
  }, [handleHideClick, popoverTriggerId]);
  useEffect(() => {
    // Listen for clicks outside of the element only when popover is shown
    if (show) {
      document.addEventListener('click', handleDocumentClick);
      return () => {
        document.removeEventListener('click', handleDocumentClick);
      };
    }
    return () => {}; // Return empty cleanup function when not shown
  }, [show, handleDocumentClick]);
  const device = useDevice();
  const isXs = device?.is?.xs ?? false;
  if (useBottomSheet && isXs) {
    return /*#__PURE__*/_jsx(BottomSheet, {
      ref: popoverRef,
      show: show,
      onHide: handleHideClick,
      buttonLabel: buttonLabel,
      className: className,
      heading: heading,
      hideCTA: hideCTA,
      renderServerSide: renderServerSide,
      ...rest,
      children: children
    });
  }
  return /*#__PURE__*/_jsx(DesktopPopover, {
    ref: popoverRef,
    show: show,
    borderColor: borderColor,
    buttonLabel: buttonLabel,
    onHide: handleHideClick,
    className: className,
    containerElement: containerElement,
    position: position,
    hideCTA: hideCTA,
    fullWidth: fullWidth,
    renderServerSide: renderServerSide,
    onBlur: event => {
      const relatedTarget = event.relatedTarget || document.activeElement;
      if (popoverRef.current && !popoverRef.current.contains(relatedTarget)) {
        handleDocumentClick(event);
      }
    },
    ...rest,
    children: children
  });
}
export default PopoverSheet;